import { DragArray } from "./DragArray.js";
import { PlanogramContainer } from "./PlanogramContainer.js";
import { PlanogramRenderer } from "./PlanogramRenderer.js";
import { PlanogramObjectAttributes, PlanogramRendererResources, PlanogramSubcomponentAttributes } from './Resources.js';
import { AttributeHelper } from "./Helpers.js";
import * as THREE from 'three';

export class PlanogramObject extends HTMLElement {

    // /**@type {SnapAnchor[]} */
    // get snapAnchors(){ return [...this.querySelectorAll("* snap-anchor")]; }
    get groupBoundingBox() {
        return new THREE.Box3().setFromObject(this.group);
        // this.partType === StageObject.NONE ? new THREE.Box3().setFromCenterAndSize(
        //     new THREE.Vector3(this.x, this.y, this.z), new THREE.Vector3(this.width, this.height, this.depth)) :
            
    }
    get worldRelativeBoundingBox(){
        const box = this.groupBoundingBox.clone();
        return new THREE.Box3().setFromCenterAndSize(new THREE.Vector3(this.x,this.y,this.z),box.getSize(new THREE.Vector3())); 
    }
    get positionOffset() { return new THREE.Vector3(); }

    //#region Attributes
    
    static Attributes = PlanogramObjectAttributes.Attributes;

    static get observedAttributes(){ return [...Object.values(PlanogramObject.Attributes)]; }
    //#endregion

    /**@type {THREE.Group} */
    #group;

    /**@type {Anchor[]} */
    get snapAnchors(){ return [...this.querySelectorAll("planogram-subcomponent")].flatMap(x=>x.snapAnchors); }

    #uid = Math.random().toString(36).substring(2, 9);
    firstRender = true;
    /**@type {PlanogramContainer} */
    #planogramContainer;

    get group() { return this.#group; }

    get name() { return this.getAttribute(PlanogramObject.Attributes.Name); }
    get x() { return Number(this.getAttribute(PlanogramObject.Attributes.X)) || 0; }
    get y() { return Number(this.getAttribute(PlanogramObject.Attributes.Y)) || 0; }
    get z() { return Number(this.getAttribute(PlanogramObject.Attributes.Z)) || 0; }
    get rotationX() { return Number(this.getAttribute(PlanogramObject.Attributes.RotationX)) || 0; }
    get rotationY() { return Number(this.getAttribute(PlanogramObject.Attributes.RotationY)) || 0; }
    get rotationZ() { return Number(this.getAttribute(PlanogramObject.Attributes.RotationZ)) || 0; }

    get rotation() { return new THREE.Euler(this.rotationX, this.rotationY, this.rotationZ); }
    get position() { return new THREE.Vector3(this.x, this.y, this.z); }
    get transparency () { return this.hasAttribute(PlanogramObject.Attributes.Transparency) ? Number(this.getAttribute(PlanogramObject.Attributes.Transparency)) : 1; }
    get lineThickness() { return Number(this.getAttribute(PlanogramObject.Attributes.LineThickness)) || 2; }
    get lineColour() { return PlanogramObject.computeColour(this.getAttribute(PlanogramObject.Attributes.LineColour), 0x000000);}
    get labelText() { return this.getAttribute(PlanogramObject.Attributes.LabelText) || null; }
    
    get selected() { return this.hasAttribute(PlanogramObject.Attributes.Selected); }
    get uid(){return this.#uid;}
    /**@type {PlanogramImage[]} */
    get images(){return [...this.querySelectorAll("planogram-image")];}

    /**@type {THREE.BufferGeometry} */
    #decoratorGeometry = new THREE.BoxGeometry(1, 1, 1);
    /**@type {THREE.InstancedMesh} */
    #decoratorMesh = null;
    #decoratorBufferSize = 0.3;
    /** @type {PlanogramObjectInteractionRule[]} */
    #interactionRules=[ ];

    ShaderMappings = new Map([]);

    setMatrix() {
        if (this.rotationX + this.rotationY + this.rotationZ !== 0) {
            this.#group.position.set(0, 0, 0);
            this.#group.rotation.set(0, 0, 0);
            this.#group.applyMatrix4(new THREE.Matrix4().makeRotationFromEuler(new THREE.Euler(-this.rotationX, -this.rotationY, -this.rotationZ)));
            this.#group.position.copy(this.position);
        } else {
            this.#group.position.copy(this.position);
            this.#group.rotation.set(0, 0, 0);
        }
        this.#group.updateMatrixWorld(true);
        this.#setDecorator();
    }

    #getMeshCount(){
        var meshCount = 1;
        if(this.tagName.toLowerCase() === "planogram-component"){
            meshCount += [...this.querySelectorAll("planogram-subcomponent")].
                filter(x=>Reflect.has(x, "firstRender") && !x.firstRender).length;
        }
        return meshCount;
    }
    

    #generateDecoratorGeometry(){
        this.#decoratorMesh?.removeFromParent();
        this.#decoratorMesh = new THREE.InstancedMesh(this.#decoratorGeometry, PlanogramRendererResources.SelectBoxShader, this.#getMeshCount());
        this.#decoratorMesh.name = "Decorator";
        this.#decoratorMesh.userData = { id:this.id };
        this.#decoratorMesh.layers.set(PlanogramRendererResources.DECORATOR_LAYER);
        this.#decoratorMesh.renderOrder = 9999;
        if (this.group) this.group.add(this.#decoratorMesh);
        this.#setDecorator();
    }

    #setDecorator(){
        if(!this.#decoratorMesh || !this.#decoratorGeometry) return;
        const mb = this.mainBlockBoundingBox;
        const boundingBoxes = [ (mb && !mb.isEmpty()) ? mb : this.groupBoundingBox ];
        const rotations = [new THREE.Vector3()];
        const selecteds = [ this.selected ? new THREE.Vector3(1,1,1) : new THREE.Vector3(0,0,0) ];

        if(this.tagName.toLowerCase() === "planogram-component"){
            const subComponents = [...this.querySelectorAll("planogram-subcomponent")].filter(x=>Reflect.has(x, "firstRender") && !x.firstRender);
            if(subComponents.length > 0){
                subComponents.forEach(subComponent => {
                    boundingBoxes.push(subComponent.realBoundingBox);
                    rotations.push(new THREE.Vector3( subComponent.rotationX, subComponent.rotationY, subComponent.rotationZ ));
                    selecteds.push(subComponent.selected ? new THREE.Vector3(1,1,1) : new THREE.Vector3(0,0,0));
                });
            }
        }

        const sizes = boundingBoxes.map(x=>x.getSize(new THREE.Vector3()));
        const positions = boundingBoxes.map(x=>x.getCenter(new THREE.Vector3()));
        for(var i=0; i< boundingBoxes.length;i++){
            const size = sizes[i];
            const position = positions[i];
            const rotation = rotations[i];
            const matrix = new THREE.Matrix4().makeTranslation(position.x, position.y, position.z).multiply(new THREE.Matrix4().makeRotationFromEuler(new THREE.Euler(
                rotation.x, rotation.y, rotation.z, 'XYZ')))
                .multiply(new THREE.Matrix4().makeScale(size.x, size.y, size.z));
            this.#decoratorMesh.setMatrixAt( i, matrix);
        }
        this.#decoratorMesh.instanceMatrix.needsUpdate = true;
        this.#decoratorGeometry.setAttribute("size", new THREE.InstancedBufferAttribute(new Float32Array(sizes.flatMap(x=>x.toArray())), 3));
        this.#decoratorGeometry.setAttribute("direction", new THREE.InstancedBufferAttribute(new Float32Array(selecteds.flatMap(x=>x.toArray())), 3));
        const mesh = this.group?.getObjectByName(Object.getPrototypeOf(this).constructor.name);
        if(mesh) mesh.userData.boundingBox = this.groupBoundingBox.clone().translate(new THREE.Vector3(this.x,this.y,this.z));
        this.#updateLabel();
    }

    setDecoratorGeometrySize( reMesh = false ){
        if(reMesh)this.#generateDecoratorGeometry();
        else this.#setDecorator();

        const mesh = this.group?.getObjectByName(Object.getPrototypeOf(this).constructor.name);
        if(mesh){
            mesh.userData.size = this.groupBoundingBox.getSize(new THREE.Vector3());
            mesh.userData.boundingBox = this.groupBoundingBox.clone().translate(new THREE.Vector3(this.x, this.y, this.z));
            this.#updateContainer();
        }
    }
    
    #updateContainer(){
        var planogramContainer = PlanogramContainer.get(this);
        if(planogramContainer.autoZoom){
            queueMicrotask(() => {
                planogramContainer.activeCameras.forEach(x=>{
                    x.updateZoom(null, true, true);
                });
            });
        }
        const isBlockingElement = (this.tagName?.toLowerCase?.() === "planogram-blocking");
        planogramContainer.querySelectorAll("planogram-blocking").forEach(x=>{
            if (Reflect.has(x, "readjustCentre")) x.readjustCentre();
            if (!isBlockingElement && Reflect.has(x, "planogramUpdated")) x.planogramUpdated();
        });
    }

    /**
     * 
     * @param {THREE.Vector3} point 
     * @returns {THREE.Vector3} the normal vector where the decorator is set
     */
    setDecoratorFromPoint( point ){
        if(!this.#decoratorMesh || !this.#decoratorGeometry || !point) return;
        const { uv:{ x, y }, normal } = this.#getUVAndNormal( point );
        const faces = this.#interactionRules[0].getAllowedDirections(normal);
        const newNormal = new THREE.Vector3(); 
        const direction = new THREE.Vector2(0,0);
        const dists = {
            [ [0,1] ] : faces.top ? (1.0 - y) : Infinity,
            [ [0,-1] ] : faces.bottom ? y : Infinity,
            [ [-1,0] ] : faces.left ? x : Infinity,
            [ [1,0] ] : faces.right ? (1.0 - x) : Infinity
        };

        if(Object.values(faces).every( d => d === null)) {
            newNormal.copy(normal);
        }else{
            let closestDist = Infinity;
            for (const [key, d] of Object.entries(dists)) {
                if (d < closestDist) {
                    closestDist = d;
                    direction.set(...key.split(",").map(Number));
                }
            }
            if(direction.equals(new THREE.Vector2(0,0))) return;
            if(normal.x !== 0) newNormal.set( 0, direction.y, direction.x );
            else if (normal.y !== 0) newNormal.set( direction.x, 0, direction.y);
            else if (normal.z !== 0) newNormal.set( direction.x, direction.y, 0);
        }

        this.#decoratorGeometry.setAttribute("direction", new THREE.InstancedBufferAttribute(new Float32Array([newNormal].flatMap(x=>x.toArray())), 3));

        return newNormal;
    }

    #getUVAndNormal( point ){
        const box = this.groupBoundingBox.clone().applyMatrix4(this.group.matrixWorld);
        const normal = new THREE.Vector3();
        const uv = new THREE.Vector2();
        const epsilon = 0.0001;
        const { x, y, z } = box.getSize(new THREE.Vector3());

        if (Math.abs(point.x - box.min.x) < epsilon) normal.set(-1, 0, 0);
        else if (Math.abs(point.x - box.max.x) < epsilon) normal.set(1, 0, 0);
        else if (Math.abs(point.y - box.min.y) < epsilon) normal.set(0, -1, 0);
        else if (Math.abs(point.y - box.max.y) < epsilon) normal.set(0, 1, 0);
        else if (Math.abs(point.z - box.min.z) < epsilon) normal.set(0, 0, -1);
        else if (Math.abs(point.z - box.max.z) < epsilon) normal.set(0, 0, 1);

        if (Math.abs(normal.x) > 0) uv.set((point.z - box.min.z) / z, (point.y - box.min.y) / y);
        else if (Math.abs(normal.y) > 0) uv.set((point.x - box.min.x) / x, (point.z - box.min.z) / z);
        else uv.set((point.x - box.min.x) / x, (point.y - box.min.y) / y);
        return { uv, normal };
    }


    /**
     * 
     * @param {THREE.Mesh|THREE.InstancedMesh} mesh 
     */
    addMesh( mesh, meshName = null ){
        if(!this.group) return;
        meshName ??= Object.getPrototypeOf(this).constructor.name; 
        if(this.group.getObjectByName(meshName))
            this.group.remove(this.group.getObjectByName(meshName));
        var size = this.groupBoundingBox.getSize(new THREE.Vector3());
        mesh.userData.size = size;
        mesh.userData.boundingBox = this.groupBoundingBox.clone();
        mesh.name = meshName;
        mesh.userData.id = this.#uid;
        mesh.userData.label = this.labelText;
        this.#group.add(mesh);
        this.#updateContainer();
        this.#updateLabel();
    }

    /**
     * 
     * @param {THREE.Mesh|THREE.InstancedMesh|THREE.Group} mesh 
     */
    addChild( child ){
        this.#group?.add(child);
    }

    /**
     * 
     * @param {THREE.Vector3} normal 
     * @param {THREE.Vector3} vector 
     * @returns {Anchor|null}
     */
    getNearestAnchor( normal, vector ){
        const face = PlanogramContainer.getFaceFromNormal(normal);
        const anchors = this.snapAnchors.filter( x => x.face === face );
        if(anchors.length === 0) return null;
        const box = this.groupBoundingBox.clone();
        const boxSize = box.getSize(new THREE.Vector3());
        const boxCentre = new THREE.Vector3(this.x,this.y,this.z);
        const middleFace = boxCentre.clone().sub(normal.clone().multiply(boxSize.clone()));
        const anchor = anchors.reduce((a,c)=> 
            c.getFacePosition(box,normal, middleFace).distanceTo(vector) <= a.getFacePosition( box, normal, middleFace ).distanceTo(vector) ? c : a );
        return anchor;
    }

    #updateLabel(){
        const mesh = this.group?.getObjectByName(Object.getPrototypeOf(this).constructor.name);
        if(mesh) mesh.userData.label = this.labelText;
        const container = this.#planogramContainer;
        if(!container) return;
        const type = this.tagName.toLowerCase() === "planogram-position" ? "product" : "component";
        const label = container.querySelector(`planogram-label[type='${type}']`);
        if(label?.updateFromPlanObject)label.updateFromPlanObject(this);
    }

    /**
     * @param {string|number} colourValue 
     * @param {number} defaultColour 
     * @returns {number}
     */
    static computeColour(colourValue, defaultColour = 0xfff, SRGB = false) {
        if (colourValue === null) return defaultColour;
        var _colourValue = Number(colourValue);
        if (SRGB) {  
            _colourValue = new THREE.Color(isNaN(_colourValue) ? colourValue : _colourValue).convertLinearToSRGB().getHex();
        }
        return _colourValue;
    }

    constructor() {
        super();
    }

    connectedCallback() {
        const rule = new PlanogramObjectInteractionRule();
        rule.faces = [ PlanogramObject.face.Left, PlanogramObject.face.Right ];
        this.#interactionRules.push( rule );
        this.#group = new THREE.Group();
        this.firstRender = false;
        this.#generateDecoratorGeometry();
        this.setMatrix();
        this.#planogramContainer = PlanogramContainer.get(this);
        this.#planogramContainer.addToScene(this.#group);
        PlanogramRenderer.applyToRelay( this, PlanogramObject.customEvents );
    }

    disconnectedCallback() { this.dispose(); }

    attributeChangedCallback(name, oldValue, newValue) {
        if (this.firstRender || oldValue === newValue) return;
        if ([PlanogramObject.Attributes.X,
            PlanogramObject.Attributes.Y,
            PlanogramObject.Attributes.Z,
            PlanogramObject.Attributes.RotationX,
            PlanogramObject.Attributes.RotationY,
            PlanogramObject.Attributes.RotationZ,
            PlanogramSubcomponentAttributes.CommonAttributes.Type].includes(name))
            this.setMatrix();
        // Keep the transform box following a selected object as its position/rotation changes.
        if (this.selected && [PlanogramObject.Attributes.X,
            PlanogramObject.Attributes.Y,
            PlanogramObject.Attributes.Z,
            PlanogramObject.Attributes.RotationX,
            PlanogramObject.Attributes.RotationY,
            PlanogramObject.Attributes.RotationZ].includes(name))
            PlanogramContainer.get(this)?.refreshRotationGizmo();
        if (name === PlanogramObject.Attributes.Selected){
            this.#decoratorGeometry.setAttribute( "direction", new THREE.Float32BufferAttribute(
                Array(24).fill((this.selected ? new THREE.Vector3(1,1,1) : new THREE.Vector3(0,0,0)).toArray()).flat(), 3 ) );
            PlanogramContainer.get(this)?.refreshRotationGizmo();
        }
        if([PlanogramObject.Attributes.X,
            PlanogramObject.Attributes.Y,
            PlanogramObject.Attributes.Z,
            PlanogramObject.Attributes.RotationX,
            PlanogramObject.Attributes.RotationY,
            PlanogramObject.Attributes.RotationZ,
            PlanogramObject.Attributes.LabelText,
            PlanogramSubcomponentAttributes.CommonAttributes.Type].includes(name))
            this.#updateLabel();
    }

    dispose() {
        const container = this.#planogramContainer;
        if(container){
            const type = this.tagName.toLowerCase() === "planogram-position" ? "product" : "component";
            const label = container.querySelector(`planogram-label[type='${type}']`);
            if(label?.generateMeshes){
                setTimeout(() => label.generateMeshes(), 0);
            }
            if (this.selected)
                container.refreshRotationGizmo();
        }
        this.#group.removeFromParent();
        this.#group = null;
        this.#planogramContainer = null;
    }


    //#region Event Raise Methods
    /**
     * 
     * @param {THREE.Vector3} oldPosition 
     * @param {THREE.Vector3} newPosition 
     * @param {DataTransfer} dataTransfer 
     */
    raiseDropEvent( oldPosition, newPosition, dataTransfer ){
        const renderer = PlanogramRenderer.get(this);
        const delta =  new THREE.Vector3().fromArray(newPosition.toArray().map(x=>Math.round(x)));
        const event = new CustomEvent( PlanogramObject.customEvents.ObjectDropped, { bubbles: true, cancelable: true, detail: { x:newPosition.x ,y:newPosition.y, z:newPosition.z } });
        
        if( this.dispatched( event ) && renderer.showMessages ){
            console.groupCollapsed("Element Dropped");
            console.log("Old Position:", oldPosition );
            console.log("New Position:", newPosition );
            console.log("Delta:", delta );
            console.log("Event: ", event);
            console.log("Target: ", this);
            console.groupEnd();
        }
    }

    /**
     * Raised when the object is transformed (resize and/or rotate) via the transform gizmo.
     * Carries the full committed transform: new world position, new rotation (radians,
     * rotation-x/y/z attribute space), and new size (world width/height/depth).
     * @param {number} px new position x
     * @param {number} py new position y
     * @param {number} pz new position z
     * @param {number} rx rotation x
     * @param {number} ry rotation y
     * @param {number} rz rotation z
     * @param {number} w new width
     * @param {number} h new height
     * @param {number} d new depth
     */
    raiseTransformEvent( px, py, pz, rx, ry, rz, w, h, d ){
        const renderer = PlanogramRenderer.get(this);
        const event = new CustomEvent( PlanogramObject.customEvents.ObjectTransformed, { bubbles: true, cancelable: true,
            detail: { px, py, pz, rx, ry, rz, w, h, d } });

        if( this.dispatched( event ) && renderer.showMessages ){
            console.groupCollapsed("Element Transformed");
            console.log("Position:", { px, py, pz } );
            console.log("Rotation:", { rx, ry, rz } );
            console.log("Size:", { w, h, d } );
            console.log("Target: ", this);
            console.groupEnd();
        }
    }

        /**
     *
     * @param {THREE.Vector3} normal
     */
    raisePositionAnchoredEvent( normal ){
        const renderer = PlanogramRenderer.get(this);
        const directionMap = [
            new THREE.Vector3(0, 1, 0),
            new THREE.Vector3(0, -1, 0),
            new THREE.Vector3(-1, 0, 0),
            new THREE.Vector3(1, 0, 0),
            new THREE.Vector3(0, 0, 1),
            new THREE.Vector3(0, 0, -1)
        ]
        const direction = directionMap.findIndex( d => d.equals(normal) );

        const event = new CustomEvent( PlanogramObject.customEvents.PositionAnchored, { bubbles: true, cancelable: true, detail: { direction: direction } });
        
        if( this.dispatched( event ) && renderer.showMessages ){
            console.groupCollapsed("Position Anchored");
            console.log("direction:", direction );
            console.log("Event: ", event);
            console.log("Target: ", this);
            console.groupEnd();
        }
        

    }

    raiseClickEvent( x, y, rightClick = false){
        const renderer = PlanogramRenderer.get(this);
        const rect = renderer.domElement.getBoundingClientRect();
        const clickEvent = new MouseEvent( "click", { bubbles: true, cancelable: true, view: window, clientX: rect.x + x, clientY: rect.y + y });
        Object.defineProperty(clickEvent, 'button', { ...Object.getOwnPropertyDescriptor(clickEvent, 'button'), get: function() {return rightClick ? 2 : 0;}});
        Object.defineProperty(clickEvent, 'target', { ...Object.getOwnPropertyDescriptor(clickEvent, 'target'), get: (function() {return this;}).bind(this)});
        if( this.dispatchEvent(clickEvent) && renderer.showMessages ){
            console.groupCollapsed("Object Selected"+ (rightClick? " and Context Menu":""));
            console.log("Target: ", this);
            console.log("Event: ", clickEvent);
            console.groupEnd();
        }
    }
    
    dispatched( event ){
        const renderer = PlanogramRenderer.get(this)?.showMessages ?? false;
        const dropped = this.dispatchEvent(event);
        return dropped && renderer;
    }
    //#endregion
    //#region Static Properties
    static getFaceFromNumber( faceNumber ){
        return faceNumber === 0 ? PlanogramObject.face.Top :
            faceNumber === 1 ? PlanogramObject.face.Bottom :
            faceNumber === 2 ? PlanogramObject.face.Left :
            faceNumber === 3 ? PlanogramObject.face.Right :
            faceNumber === 4 ? PlanogramObject.face.Front :
            PlanogramObject.face.Back;
    }

    /**@enum {string} */
    static face ={
        Top: "top",
        Bottom: "bottom",
        Left: "left",
        Right: "right",
        Front: "front",
        Back: "back"
    }

    static customEvents = {
        ObjectDropped: "objectdropped",
        PositionAnchored: "positionanchored",
        ObjectTransformed: "objecttransformed"
    }
    //#endregion
}

export class Anchor{
    face;
    direction;
    x;
    y;
    width;
    height;
    forceConstraint;
    /**
     * 
     * @param {string} face 
     * @param {string} direction 
     * @param {number} x 
     * @param {number} y 
     * @param {number} width 
     * @param {number} height 
     * @param {boolean} forceConstraint 
     */
    constructor( face, direction, x, y, width, height, forceConstraint ){
        this.face = face === "f" ? PlanogramObject.face.Front :
            face === "b" ? PlanogramObject.face.Back :
            face === "l" ? PlanogramObject.face.Left :
            face === "r" ? PlanogramObject.face.Right :
            face === "t" ? PlanogramObject.face.Top :
            face === "n" ? PlanogramObject.face.Bottom : PlanogramObject.face.Front;
        
        this.direction = direction === "t" ? Anchor.direction.Top :
            direction === "b" ? Anchor.direction.Bottom :
            direction === "l" ? Anchor.direction.Left :
            direction === "r" ? Anchor.direction.Right : Anchor.direction.Top;

        this.x = x;
        this.y = y;
        this.width = width;
        this.height = height;
        this.forceConstraint = forceConstraint;
    }

    /**
     * @param {THREE.Box3} box
     * @param {THREE.Vector3} normal 
     * @param {THREE.Vector3} vector 
     * @param {THREE.Box3} size 
     * @returns 
     */
    getFacePosition( box, normal, vector, size = new THREE.Vector3(0,0,0) ){
        const boxSize = box.getSize(new THREE.Vector3());
        const boxCentre = box.getCenter(new THREE.Vector3());
        const translatedPostion = new THREE.Vector3();
        const v = new THREE.Vector2(this.x !== null? (boxCentre.x + this.x  + boxSize.x/2 + size.x/2): vector.x, 
            this.y !== null? (boxCentre.y + this.y + size.y/2) - (boxSize.y/2)  : vector.y);
        if(normal.getComponent(0)){
            translatedPostion.setComponent(0 , vector.x ).add(new THREE.Vector3(0, v.x, v.y));
        }else if(normal.getComponent(1)){
            translatedPostion.setComponent(1 , vector.y ).add(new THREE.Vector3(v.x, 0,  v.y));
        }else{
            translatedPostion.setComponent(2 , vector.z ).add(new THREE.Vector3(v.x, v.y, 0));
        }
        return translatedPostion;
    }

    /**
     * 
     * @param {string} anchorString 
     * @returns {Anchor[]}
     */
    static fromString( anchorString ){
        if(!anchorString) return [];
        function computeIncremental( b, dir, inc, i ){
            const base = Number(b);
            if(!dir || !inc) return base;
            const increment = Number(inc);
            return dir.toUpperCase() === "I" ? base + (increment * i) : base - (increment * i);
        }

        const anchors = [];
        const regexp = /(?:(?:p)(?<repeat>\d+))?(?<face>(?:[fblrtn]))(?<x>\d+(?:\.\d+)?)(?:(?<xd>(?:[id]))(?<xi>\d+(?:\.\d+)?))?\s(?<y>\d+(?:\.\d+)?)(?:(?<yd>(?:[id]))(?<yi>\d+(?:\.\d+)?))?\s(?<width>\d+(?:\.\d+)?)(?:(?<widthd>(?:[id]))(?<widthi>\d+(?:\.\d+)?))?\s(?<height>\d+(?:\.\d+)?)(?:(?<heightd>(?:[id]))(?<heighti>\d+(?:\.\d+)?))?(?<direction>[tblr])?/gi;
        for(var match = regexp.exec(anchorString); match;match = regexp.exec(anchorString)){
            const face = match.groups["face"].toLowerCase();
            const forceConstraint = match.groups["face"] === match.groups["face"].toUpperCase();
            const repeat = match.groups["repeat"] ? Number(match.groups["repeat"]) : 1;
            const direction = match.groups["direction"]?.toLowerCase();
            const output = new Array(repeat).fill(0).map((v,i) => {
                return new Anchor(
                    face,
                    direction,
                    computeIncremental(match.groups["x"], match.groups["xd"], match.groups["xi"], i),
                    computeIncremental(match.groups["y"], match.groups["yd"], match.groups["yi"], i),
                    computeIncremental(match.groups["width"], match.groups["widthd"], match.groups["widthi"], i),
                    computeIncremental(match.groups["height"], match.groups["heightd"], match.groups["heighti"], i),
                    forceConstraint
                );
            });
            anchors.push(...output);
        }

        return anchors;
    }
    static direction = {
        Top: "top",
        Bottom: "bottom",
        Left: "left",
        Right: "right"
    }
}

export class PlanogramObjectInteractionRule {
    //Target of the rule, e.g I'm a planogram-position 
    targetType = null;
    targetRule = null;
    faces = [];

    constructor(){ }
    
    getAllowedDirections( normal ){
        /** @type {string[]} */
        const faces = this.faces.reduce((a,c)=> a.includes(c) ? a : [ ...a, c ],[]);
        const face = PlanogramContainer.getFaceFromNormal(normal);
        const faceDirectionMap = {
            [PlanogramObject.face.Top]: { bottom: PlanogramObject.face.Front, top: PlanogramObject.face.Back, left: PlanogramObject.face.Left, right: PlanogramObject.face.Right },
            [PlanogramObject.face.Bottom]: { top: PlanogramObject.face.Front, bottom: PlanogramObject.face.Back, left: PlanogramObject.face.Left, right: PlanogramObject.face.Right },
            [PlanogramObject.face.Left]: { right: PlanogramObject.face.Front, left: PlanogramObject.face.Back, top: PlanogramObject.face.Top, bottom: PlanogramObject.face.Bottom },
            [PlanogramObject.face.Right]: { left: PlanogramObject.face.Front, right: PlanogramObject.face.Back, top: PlanogramObject.face.Top, bottom: PlanogramObject.face.Bottom },
            [PlanogramObject.face.Front]: { left: PlanogramObject.face.Left, right: PlanogramObject.face.Right, top: PlanogramObject.face.Top, bottom: PlanogramObject.face.Bottom },
            [PlanogramObject.face.Back]: { right: PlanogramObject.face.Left, left: PlanogramObject.face.Right, top: PlanogramObject.face.Top, bottom: PlanogramObject.face.Bottom }
        };
        
        const allowedDirections = { [PlanogramObject.face.Top]: true, [PlanogramObject.face.Left]: true, [PlanogramObject.face.Right]: true, [PlanogramObject.face.Bottom]: true };
        const mappings = faceDirectionMap[face];
        if(!mappings) return allowedDirections;
        for ( const [direction, requiredFace] of Object.entries(mappings))
            allowedDirections[direction] = faces.includes(requiredFace);

        if(Object.values(allowedDirections).every(v => v === false) && faces.includes(face)){
            allowedDirections[PlanogramObject.face.Top] = null;
            allowedDirections[PlanogramObject.face.Bottom] = null;
            allowedDirections[PlanogramObject.face.Left] = null;
            allowedDirections[PlanogramObject.face.Right] = null;
        }
        return allowedDirections;
    }

}

export class PlanogramObjectShaderDataManager{

    /**@type {THREE.DataTexture} */
    #texture;
    /**@type {THREE.ShaderMaterial} */
    #shader = null;

    get texture(){ return this.#texture; }

    /**
     *
     * @param {THREE.ShaderMaterial} shader
     */
    constructor( shader ){
        this.#shader = shader;
    }

    /**
     * Every row in the objectData texture (one per subcomponent, one per divider) occupies this
     * many floats - it must match the shader's hardcoded `stride` (in vec4s) in Resources.js.
     */
    static blockSize = 52;

    /**
     * Assigns each subcomponent - and each of its dividers - a sequential row index into the
     * objectData texture. Dividers get their own row (rather than sharing their parent's) so the
     * shader can look up divider-specific colour/fill data via texelPositionAndType.
     * Must be built from the same container-wide, ordered subcomponent list used by updateData(),
     * so callers (e.g. PlanogramComponent) resolve to the same indices as the texture they read.
     * @param {PlanogramSubcomponent[]} subcomponents
     * @returns {Map<PlanogramSubcomponent|PlanogramDivider, number>}
     */
    static buildBlockIndices( subcomponents ){
        const indices = new Map();
        let blockIndex = 0;
        for(const sub of subcomponents){
            indices.set(sub, blockIndex++);
            for(const divider of sub.dividers) indices.set(divider, blockIndex++);
        }
        return indices;
    }

    /**
     * Updates texture data from subcomponents (and their dividers) in the provided container
     * @param {PlanogramContainer} planogramContainer
     */
    updateData( planogramContainer ){
        if(!planogramContainer) return;

        // Find all PlanogramSubcomponents in order
        const subcomponents = [...planogramContainer.querySelectorAll("planogram-subcomponent")].filter(x=>Reflect.has(x, "firstRender") && !x.firstRender && x.isConnected);
        if(subcomponents.length === 0) return;

        const blockSize = PlanogramObjectShaderDataManager.blockSize;
        const blocks = subcomponents.flatMap(sub => [sub.registryValues, ...sub.dividers.map(divider => divider.registryValues)]);
        const registryValues = blocks.flatMap(values => {
            const truncated = values.slice(0, blockSize);
            return [...truncated, ...Array(blockSize - truncated.length).fill(0)];
        }).filter(x => x != null);

        const stride = blockSize / 4;
        const rowLength = registryValues.length / 4;
        const textureWidth = PlanogramObjectShaderDataManager.maxRestriction.width;
        const columnLength = Math.max(1, Math.ceil(rowLength / textureWidth));

        if( registryValues.length > 0 ){
            const data = new Float32Array( textureWidth * columnLength * 4 );
            data.set(registryValues);
            this.#createTexture( data, stride, textureWidth, columnLength );
        }

    }

    /**
     *
     * @param {Float32Array} data
     * @param {number} height
     * @param {number} width
     */
    #createTexture( data, stride = 1, width = PlanogramObjectShaderDataManager.maxRestriction.width, height = 1 ){

        const texture = new THREE.DataTexture(data, width, height, THREE.RGBAFormat, THREE.FloatType);
        texture.magFilter = THREE.NearestFilter;
        texture.minFilter = THREE.NearestFilter;
        texture.wrapS = THREE.ClampToEdgeWrapping;
        texture.wrapT = THREE.ClampToEdgeWrapping;
        texture.generateMipmaps = false;

        if(!this.#texture){
            this.#shader.uniforms = { ...this.#shader.uniforms,
                [PlanogramObjectShaderDataManager.uniformName]: { value: texture },
                [PlanogramObjectShaderDataManager.strideUniformName]: { value: stride }
            };
        }else{
            this.#texture.dispose();
            this.#shader.uniforms[PlanogramObjectShaderDataManager.uniformName].value = texture;
            this.#shader.uniforms[PlanogramObjectShaderDataManager.strideUniformName].value = stride;
        }

        texture.image = { data, width, height };
        this.#texture = texture;
        this.#texture.needsUpdate = true;
    }

    /**
     *
     * @param {PlanogramContainer} planogramContainer
     * @param {string} uid
     * @returns {number|null}
     */
    getObjectIndex( planogramContainer, uid ){
        if(!planogramContainer) return null;
        const subcomponents = [...planogramContainer.querySelectorAll("planogram-subcomponent")];
        return subcomponents.findIndex(sub => sub.uid === uid);
    }

    static uniformName = "objectData";
    static strideUniformName = "stride";

    static maxRestriction = {
        width: 2048,
        height: 2048,
    }

}